/**
 * User Profile API — backend calls for the V2 user profile page.
 *
 * READ: getuserprofile, att_user_roles, getuserlogs,
 *       dashboardhourchart, dashboardweekchart, eventReasonSummary, getlimitedevents
 * WRITE: edit / resetPassword / delete all delegate to ../../api/usersApi.js,
 *        which owns the cloud vs legacy payload shaping for both pages.
 */

import { apiFetch, apiReport, apiTransaction } from '../../../../services/api.js';
import { getCampusConfig, getSession } from '../../../../services/session.js';
import {
  resetPassword as resetPasswordShared,
  editUser as editUserShared,
  deleteUsers as deleteUsersShared,
} from '../../api/usersApi.js';
import { campusToGuid } from '../../../../libs.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true if the current campus is cloud-enabled */
function isCloud() {
  return !!getCampusConfig()?.is_cloud;
}

/** Returns the campus GUID for legacy payloads */
function getGuid(campusId) {
  const session = getSession();
  return campusToGuid(session, campusId);
}

// ---------------------------------------------------------------------------
// READ — Profile Data
// ---------------------------------------------------------------------------

export function fetchUserProfile(campusId, userId) {
    return apiFetch({
        procedure: 'getuserprofile',
        reqType: 'stored',
        payload: { user_filter: userId, campus_id: campusId, is_cloud: isCloud(), campus_guid: getGuid(campusId)},
    });
}

export function fetchUserBadges(campusId, userId) {
    return apiFetch({
        procedure: 'getuserbadges',
        reqType: 'stored',
        payload: { user_filter: userId, campus_id: campusId },
    });
}

export function fetchUserRoles(campusId) {
    return apiFetch({
        table: 'att_user_roles',
        procedure: 'fetchall',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

export function fetchUserLogs(userId) {
    return apiFetch({
        procedure: 'getuserlogs',
        reqType: 'stored',
        pKey: 'user_id',
        pVal: userId,
    });
}

export function fetchPreformNotes(campusId) {
    return apiFetch({
        table: 'core_preform_event_notes',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// READ — Reporting Data
// ---------------------------------------------------------------------------

export function fetchHourChart(campusId, userId) {
    return apiReport({
        repType: 'dashboardhourchart',
        campusId,
        filters: { userFilter: userId.toString() },
    });
}

export function fetchWeekChart(campusId, userId) {
    return apiReport({
        repType: 'dashboardweekchart',
        campusId,
        filters: { userFilter: userId.toString() },
    });
}

export function fetchReasonSummary(campusId, userId, startDate, endDate) {
    return apiReport({
        repType: 'eventReasonSummary',
        campusId,
        startDate,
        endDate,
        filters: { userFilter: userId.toString() },
        // See areaProfileApi.fetchReasonSummary. getCampusConfig(campusId) rather
        // than the file's isCloud() helper: that one reads the session's current
        // campus, which is right for user-manage but not for a report scoped to
        // an explicit campusId.
        isCloud: Boolean(getCampusConfig(campusId)?.is_cloud),
    });
}

export function fetchLimitedEvents(campusId, userId) {
    return apiFetch({
        procedure: 'getlimitedevents',
        reqType: 'stored',
        payload: { campus_id: campusId, user_id: userId },
    });
}

// ---------------------------------------------------------------------------
// WRITE — User Management
// ---------------------------------------------------------------------------

/**
 * Edit user profile (cloud or legacy).
 *
 * Delegates to the shared usersApi.editUser, the same way resetUserPassword
 * below delegates to resetPassword, so the cloud/legacy payload shaping lives
 * in one place.
 *
 * The previous implementation spread this page's snake_case payload straight
 * into the legacy request with only `Action` in PascalCase. User_Manager.py
 * reads UserId/DisplayName/CampusId/Migrated/CampusGuid/CognitoId/UserRole/
 * GroupIds/PortalAccess/Username/UserEmail/PhoneNumber, so every identity field
 * arrived empty, `if userId:` was falsy and the update was skipped outright.
 * It failed closed rather than writing blanks, but the error branch is gated on
 * `if campusId:` -- also missing -- so the Lambda returned an empty Status and
 * the page could only report "Unknown Error".
 *
 * It looked almost right because User_Manager.py reads a MIXED convention:
 * title / is_grandstream / sip_extension / wave_password are snake_case there
 * and did land. Do not re-introduce a hand-rolled payload here.
 *
 * @param {object} payload — new user data (snake_case, as the page holds it)
 * @param {object} oldPayload — original user data (for audit)
 * @param {boolean} _isCloud — accepted for back-compat; ignored. editUser
 *                             derives isCloud from getCampusConfig() itself.
 * @param {object} session — { userId, clientId, campusId? }
 * @returns {Promise<[boolean, string?]>}
 */
export async function editUserProfile(payload, oldPayload, _isCloud, session) {
    return editUserShared(payload, oldPayload, {
        ...session,
        campusId: session?.campusId ?? payload.campus_id,
    });
}

/**
 * Reset user password.
 *
 * Delegates to the shared usersApi.resetPassword so the cloud/legacy payload
 * shaping (including the PascalCase key map and `Migrated: true` flag the
 * legacy backend requires) lives in one place. The previous in-line spread
 * implementation silently failed on legacy campuses because the user-profile
 * page passes snake_case keys that the legacy endpoint doesn't recognize.
 *
 * @param {object} payload  — { user_id, username, password, campus_id?, ... }
 * @param {boolean} _isCloud — accepted for back-compat; ignored. resetPassword
 *                             derives isCloud from getCampusConfig() itself.
 * @param {object} session   — { userId, clientId, campusId? }
 */
export async function resetUserPassword(payload, _isCloud, session) {
    return resetPasswordShared(
        {
            user_id: payload.user_id,
            username: payload.username,
            password: payload.password,
        },
        {
            ...session,
            campusId: session?.campusId ?? payload.campus_id,
        }
    );
}

/**
 * Delete user.
 *
 * Same delegation, and same reason: the hand-rolled legacy payload here had the
 * identical casing bug as editUserProfile. deleteUsers takes a list and sources
 * is_grandstream from the campus config itself.
 *
 * @param {object} payload — { user_id, username, cognito_id, sip_extension, ... }
 * @param {boolean} _isCloud — accepted for back-compat; ignored.
 * @param {object} session — { userId, clientId, campusId? }
 */
export async function deleteUser(payload, _isCloud, session) {
    return deleteUsersShared([payload], {
        ...session,
        campusId: session?.campusId ?? payload.campus_id,
    });
}

/**
 * Add event note (for reporting tab).
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section C): Admin+; notes must carry the request's campus. */
export async function addEventNote(eventId, campusId, note, userId, clientId) {
    return apiTransaction({
        transType: 'event_note_add',
        payload: [{ event_id: eventId, campus_id: campusId, note, modified_by: userId }],
        campusId,
        clientId,
        userId,
    });
}
